import { useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Github, Wallet } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

/**
 * SPA signin entry. Two columns:
 *
 *   - OAuth: links to backend's /auth/start?provider=...&redirect_to=...
 *     Brief 63 + 65 own the full OAuth dance server-side: the api
 *     Lambda talks directly to Google/GitHub, exchanges the code,
 *     mints rs_session, and 302s into the SPA at the redirect_to
 *     URL (defaulting to `<origin>/dashboard`). No SPA-side
 *     /auth/callback route — the SPA's first render runs the
 *     cold-reload probe with the cookie already set.
 *   - Wallet: links to /wallet-signin (separate page so we don't pull
 *     the wagmi+viem bundle on the OAuth-only path).
 *
 * The `redirect_to` we pass to /auth/start is the user's intended
 * destination (`?next=` round-trip, defaulting to `<origin>/dashboard`)
 * — never a /auth/callback URL.
 */

/**
 * Telegram Login Widget — Brief 145 / Courier Phase A.
 *
 * Telegram serves a JS widget that renders a Telegram-branded "Log in
 * with Telegram" iframe button. The script must execute against a real
 * DOM node, so we inject it via useEffect rather than as JSX. On user
 * confirmation in Telegram, the widget 302s the browser to
 * `data-auth-url` with the auth payload as query params; CloudFront
 * proxies `/auth/*` on app.relaystation.ai to the API Gateway, so the
 * server-side `verifyTelegramLoginPayload` validates HMAC and mints
 * the session cookie.
 *
 * Bot username + `data-auth-url` domain are bound by the operator via
 * `@BotFather /setdomain app.relaystation.ai`. Any mismatch makes the
 * widget render but the auth flow silently fail at the callback.
 *
 * data-request-access="write" requests permission for the bot to DM
 * the user after signup (welcome message, top-up confirmations). The
 * user can deny it; signup still succeeds, but bot DMs to a user who
 * never opens the bot will fail until they /start it manually.
 *
 * Limitation: this path does NOT round-trip the `?next=` query param
 * the Google/GitHub buttons honor. Telegram-signups always land at
 * /dashboard. Adding `next` would require a separate carrier (state
 * cookie or LocalStorage) because Telegram's HMAC validation rejects
 * any extra query param it didn't sign. Deferred until Phase B has a
 * concrete need.
 */
function TelegramLoginButton() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', 'relaystation_courier_bot');
    script.setAttribute('data-size', 'large');
    script.setAttribute(
      'data-auth-url',
      `${window.location.origin}/auth/telegram/callback`,
    );
    script.setAttribute('data-request-access', 'write');
    node.appendChild(script);

    return () => {
      // Clean up the injected script + the iframe button on unmount,
      // so re-mounting (e.g. via react-router navigation back to /signin)
      // doesn't stack multiple buttons.
      while (node.firstChild) {
        node.removeChild(node.firstChild);
      }
    };
  }, []);

  return <div ref={containerRef} className="flex justify-start" />;
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M21.6 12.227c0-.71-.064-1.391-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.997 3.022v2.51h3.232c1.891-1.74 2.983-4.305 2.983-7.355z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.964-.895 6.617-2.418l-3.232-2.51c-.895.6-2.04.955-3.385.955-2.605 0-4.81-1.76-5.595-4.123H3.064v2.59A9.997 9.997 0 0 0 12 22z"
      />
      <path
        fill="#FBBC05"
        d="M6.405 13.904a6.005 6.005 0 0 1 0-3.809V7.505H3.064a10.005 10.005 0 0 0 0 8.99l3.34-2.59z"
      />
      <path
        fill="#EA4335"
        d="M12 5.977c1.469 0 2.786.505 3.823 1.496l2.866-2.867C16.96 2.992 14.696 2 12 2A9.997 9.997 0 0 0 3.064 7.505l3.34 2.59C7.19 7.737 9.395 5.977 12 5.977z"
      />
    </svg>
  );
}

// Brief 259 — telegram-login is decommissioned for launch (single greppable
// client const, mirrors the server-side TELEGRAM_LOGIN_STARS_ENABLED). The
// Telegram sign-in card is hidden; flip to `true` + redeploy to restore. The
// Courier "Connect Telegram" flow (Account tab) is a different thing and stays.
const TELEGRAM_LOGIN_ENABLED = false;

export function SignIn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const reason = searchParams.get('reason');

  // Whatever URL the user was trying to reach before the redirect.
  // /v1/account 401 sends them here with reason=session_expired; we
  // round-trip the original path via `?next=` if present.
  const next = searchParams.get('next') ?? '/dashboard';

  const startUrl = (provider: 'google' | 'github') => {
    // Pass the user's intended landing URL (next, default /dashboard)
    // as redirect_to. Post-brief-63 the server-side /auth/callback
    // handler validates redirect_to against the API's allowlist and
    // 302s the browser there directly with rs_session set.
    const target = `${window.location.origin}${next}`;
    return `/auth/start?provider=${provider}&redirect_to=${encodeURIComponent(
      target,
    )}`;
  };

  return (
    <main className="container flex min-h-screen items-center justify-center py-16">
      <div className="w-full max-w-3xl space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-semibold tracking-tight">
            Sign in to Relaystation
          </h1>
          <p className="mt-2 text-muted-foreground">
            {TELEGRAM_LOGIN_ENABLED
              ? 'Three ways in. Pick whichever fits how you build.'
              : 'Two ways in — OAuth for humans, a wallet for agents and crypto-native devs.'}
          </p>
        </div>

        {reason === 'session_expired' ? (
          <Alert>
            <AlertTitle>Your session expired</AlertTitle>
            <AlertDescription>
              Sign in again to pick up where you left off.
            </AlertDescription>
          </Alert>
        ) : null}

        <div className={`grid gap-4 ${TELEGRAM_LOGIN_ENABLED ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          <Card className="flex flex-col h-full">
            <CardHeader>
              <CardTitle>OAuth</CardTitle>
              <CardDescription>
                Email + identity from Google or GitHub. Best for humans
                building scripts and integrations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start gap-3"
                asChild
              >
                <a href={startUrl('google')}>
                  <GoogleGlyph />
                  Sign in with Google
                </a>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-3"
                asChild
              >
                <a href={startUrl('github')}>
                  <Github className="h-4 w-4" />
                  Sign in with GitHub
                </a>
              </Button>
            </CardContent>
          </Card>

          {/* Brief 259 — telegram-login decommissioned for launch; card hidden
              (code retained behind TELEGRAM_LOGIN_ENABLED for the re-enable path). */}
          {TELEGRAM_LOGIN_ENABLED && (
            <Card className="flex flex-col h-full">
              <CardHeader>
                <CardTitle>Telegram</CardTitle>
                <CardDescription>
                  Sign in with your Telegram account. Top up with Stars in
                  the bot; one balance funds every Relaystation product.
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-auto">
                <TelegramLoginButton />
              </CardContent>
            </Card>
          )}

          <Card className="flex flex-col h-full">
            <CardHeader>
              <CardTitle>Wallet</CardTitle>
              <CardDescription>
                Sign with an EVM wallet (MetaMask, Rabby, Coinbase Wallet,
                etc.). Best for agents and crypto-native devs.
              </CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <Button
                className="w-full justify-start gap-3"
                onClick={() =>
                  navigate(
                    next === '/dashboard'
                      ? '/wallet-signin'
                      : `/wallet-signin?next=${encodeURIComponent(next)}`,
                  )
                }
              >
                <Wallet className="h-4 w-4" />
                Sign in with wallet
              </Button>
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          New to Relaystation?{' '}
          <Link to="/signin" className="underline">
            Just sign in
          </Link>{' '}
          — your account is created on first OAuth signin.
        </p>
      </div>
    </main>
  );
}
